import {
    Avatar,
    Box,
    Button,
    Flex,
    HStack,
    Icon,
    IconButton,
    Spinner,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Tag,
    Text,
    Textarea,
    Tooltip,
    useToast,
    VStack,
} from '@chakra-ui/react';
import AppDrawer from '../components/AppDrawer';
import { Fragment, KeyboardEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TbArrowLeft, TbArrowUp, TbRefresh } from 'react-icons/tb';
import { Icon as YaxleIcon } from '../ui';
import { SupplierNote, SupplierOrderStatus } from '../hooks/useGetOrderDetail';
import { formatPhoneNumber } from '../functions';
import SessionContext from '../contexts/SessionContext';
import { SIDE_DRAWER_CONTAINER_PROPS, INSTANT_SIDE_DRAWER_MOTION_PROPS } from './sideDrawerContainerProps';
import SourceBadge from '../components/SourceBadge';
import SupplierOrderInfoPopover from '../components/SupplierOrderInfoPopover';
import {
    SupplierOrderMessage,
    useAddSupplierOrderComment,
    useGetSupplierOrderComments,
} from '../hooks/useSupplierOrderComments';

export interface SupplierOrderSummary {
    supplierOrderId: string;
    supplierName: string;
    status: SupplierOrderStatus;
    phoneNumber: string | null;
    email: string | null;
    source: 'partstrader' | 'opstrax' | 'ccc';
    displayId?: string | number | null;
}

type SupplierNotesModalTab = 'quoting-comments' | 'order-messages';

const getSupplierStatusLabel = (status: SupplierOrderStatus) => {
    switch (status) {
        case 'confirmed':
            return 'Confirmed';
        case 'ordered':
            return 'Ordered';
        case 'rejected':
            return 'Rejected';
        case 'cancelled':
            return 'Cancelled';
    }
};

const getStatusColor = (status: SupplierOrderStatus) => {
    switch (status) {
        case 'confirmed':
            return 'purple';
        case 'ordered':
            return 'teal';
        case 'rejected':
            return 'gray';
        case 'cancelled':
            return 'red';
    }
};

const formatMessageTime = (dateString: string) =>
    new Date(dateString).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

const formatDayDivider = (dateString: string) => {
    const d = new Date(dateString);
    const sameYear = d.getFullYear() === new Date().getFullYear();
    return d.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        ...(sameYear ? {} : { year: 'numeric' }),
    });
};

const isSameDay = (a: string, b: string) => {
    const da = new Date(a);
    const db = new Date(b);
    return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
};


interface SupplierNotesModalProps {
    isOpen: boolean;
    notes: Array<SupplierNote>;
    onClose: () => void;
    /** Parent order id — used to open the selected supplier thread on /chat. */
    orderId?: string;
    supplierOrders?: Array<SupplierOrderSummary>;
    initialTab?: SupplierNotesModalTab;
    initialSupplierOrderId?: string | null;
    embedded?: boolean;
    hideOverlay?: boolean;
}

const SupplierNotesModal = ({
    isOpen,
    notes,
    onClose,
    orderId,
    supplierOrders: supplierOrdersProp,
    initialTab = 'quoting-comments',
    initialSupplierOrderId = null,
    embedded = false,
    hideOverlay = false,
}: SupplierNotesModalProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const toast = useToast();
    const sessionContext = useContext(SessionContext);
    /** Hide when already viewing this thread on the full Chats page. */
    const showOpenInChats = !!orderId && location.pathname !== '/chat';
    const { mutateAsync: fetchSupplierComments } = useGetSupplierOrderComments();
    const { mutateAsync: sendSupplierComment, isPending: isSendingMessage } = useAddSupplierOrderComment();
    const supplierOrders = useMemo(
        () => supplierOrdersProp ?? [],
        [supplierOrdersProp]
    );
    const hasSupplierOrders = supplierOrders.length > 0;
    const [activeTab, setActiveTab] = useState<SupplierNotesModalTab>('quoting-comments');
    const [selectedSupplierOrderId, setSelectedSupplierOrderId] = useState<string | null>(null);
    const [newMessage, setNewMessage] = useState('');
    const [isLoadingAllMessages, setIsLoadingAllMessages] = useState(false);
    const [loadingBySupplierOrderId, setLoadingBySupplierOrderId] = useState<Record<string, boolean>>({});
    const [messagesBySupplierOrderId, setMessagesBySupplierOrderId] = useState<Record<string, Array<SupplierOrderMessage>>>({});
    const [failedSupplierOrderIds, setFailedSupplierOrderIds] = useState<Set<string>>(new Set());
    const hasFetchedMessagesForOpenRef = useRef(false);

    const selectedSupplierOrder = useMemo(
        () => supplierOrders.find((order) => order.supplierOrderId === selectedSupplierOrderId) ?? null,
        [supplierOrders, selectedSupplierOrderId]
    );

    const openInChats = useCallback(() => {
        if (!orderId) return;
        onClose();
        navigate('/chat', {
            state: {
                newChat: {
                    mode: 'supplier' as const,
                    orderId,
                    ...(selectedSupplierOrder
                        ? { supplierKey: selectedSupplierOrder.supplierOrderId }
                        : {}),
                },
            },
        });
    }, [navigate, onClose, orderId, selectedSupplierOrder]);

    const selectedSupplierOrderMessages = selectedSupplierOrder
        ? (messagesBySupplierOrderId[selectedSupplierOrder.supplierOrderId] ?? [])
        : [];

    const totalSupplierMessageCount = useMemo(() => {
        return Object.values(messagesBySupplierOrderId).reduce(
            (total, messages) => total + messages.length,
            0
        );
    }, [messagesBySupplierOrderId]);

    const fetchMessagesForSupplierOrder = useCallback(async (
        supplierOrder: SupplierOrderSummary,
        options?: { forceRefresh?: boolean; }
    ) => {
        setLoadingBySupplierOrderId((prev) => ({ ...prev, [supplierOrder.supplierOrderId]: true }));
        try {
            const messages = await fetchSupplierComments({
                supplierOrderId: supplierOrder.supplierOrderId,
                forceRefresh: options?.forceRefresh === true,
            });
            setMessagesBySupplierOrderId((prev) => ({ ...prev, [supplierOrder.supplierOrderId]: messages }));
            setFailedSupplierOrderIds((prev) => {
                const next = new Set(prev);
                next.delete(supplierOrder.supplierOrderId);
                return next;
            });
        } catch {
            setMessagesBySupplierOrderId((prev) => ({ ...prev, [supplierOrder.supplierOrderId]: [] }));
            setFailedSupplierOrderIds((prev) => {
                const next = new Set(prev);
                next.add(supplierOrder.supplierOrderId);
                return next;
            });
        } finally {
            setLoadingBySupplierOrderId((prev) => ({ ...prev, [supplierOrder.supplierOrderId]: false }));
        }
    }, [fetchSupplierComments]);

    const fetchAllMessages = useCallback(async (options?: { forceRefresh?: boolean; }) => {
        if (!hasSupplierOrders) return;
        setIsLoadingAllMessages(true);
        try {
            await Promise.all(
                supplierOrders.map((supplierOrder) => fetchMessagesForSupplierOrder(supplierOrder, options))
            );
        } finally {
            setIsLoadingAllMessages(false);
        }
    }, [fetchMessagesForSupplierOrder, hasSupplierOrders, supplierOrders]);

    useEffect(() => {
        if (!isOpen) {
            hasFetchedMessagesForOpenRef.current = false;
            return;
        }
        setActiveTab(initialTab === 'order-messages' && hasSupplierOrders ? 'order-messages' : 'quoting-comments');
        if (
            initialSupplierOrderId &&
            supplierOrders.some((order) => order.supplierOrderId === initialSupplierOrderId)
        ) {
            setSelectedSupplierOrderId(initialSupplierOrderId);
        } else {
            setSelectedSupplierOrderId(null);
        }
        setNewMessage('');
    }, [hasSupplierOrders, initialSupplierOrderId, initialTab, isOpen, supplierOrders]);

    useEffect(() => {
        if (
            !isOpen ||
            !hasSupplierOrders ||
            activeTab !== 'order-messages' ||
            hasFetchedMessagesForOpenRef.current
        ) {
            return;
        }
        hasFetchedMessagesForOpenRef.current = true;
        void fetchAllMessages();
    }, [activeTab, fetchAllMessages, hasSupplierOrders, isOpen]);

    const handleRefreshMessages = useCallback(async () => {
        if (selectedSupplierOrder) {
            await fetchMessagesForSupplierOrder(selectedSupplierOrder, { forceRefresh: true });
            return;
        }
        await fetchAllMessages({ forceRefresh: true });
    }, [fetchAllMessages, fetchMessagesForSupplierOrder, selectedSupplierOrder]);

    const currentUserFullName = sessionContext?.isAuthenticated
        ? `${sessionContext.firstName ?? ''} ${sessionContext.lastName ?? ''}`.trim()
        : '';

    const handleSendMessage = async () => {
        if (!selectedSupplierOrder || !newMessage.trim() || !sessionContext?.sessionId) return;

        const trimmedMessage = newMessage.trim();
        const optimisticId = `optimistic-${Date.now()}`;
        const optimisticMessage: SupplierOrderMessage = {
            _id: optimisticId,
            partstraderCommentId: Date.now(),
            comment: trimmedMessage,
            fromSupplier: false,
            senderName: null,
            sentByUserId: sessionContext?.isAuthenticated ? sessionContext.userId : undefined,
            sentByUserName: currentUserFullName || undefined,
            timestamp: new Date().toISOString(),
        };

        setMessagesBySupplierOrderId((prev) => {
            const existing = prev[selectedSupplierOrder.supplierOrderId] ?? [];
            return {
                ...prev,
                [selectedSupplierOrder.supplierOrderId]: [...existing, optimisticMessage],
            };
        });
        setNewMessage('');
        try {
            await sendSupplierComment({
                supplierOrderId: selectedSupplierOrder.supplierOrderId,
                comment: trimmedMessage,
            });
        } catch {
            setMessagesBySupplierOrderId((prev) => {
                const existing = prev[selectedSupplierOrder.supplierOrderId] ?? [];
                return {
                    ...prev,
                    [selectedSupplierOrder.supplierOrderId]: existing.filter((message) => message._id !== optimisticId),
                };
            });
            setNewMessage(trimmedMessage);
            toast({
                title: 'Failed to send message',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleMessageKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            void handleSendMessage();
        }
    };

    const tabsContent = (
            <Box p={0} display="flex" flexDirection="column" minH={0} flex={1} bg="white">
                <Tabs
                    index={activeTab === 'quoting-comments' ? 0 : 1}
                    onChange={(index) => {
                        if (index === 1 && !hasSupplierOrders) return;
                        setActiveTab(index === 0 ? 'quoting-comments' : 'order-messages');
                    }}
                    display="flex"
                    flexDirection="column"
                    flex={1}
                    minH={0}
                    bg="white"
                >
                    <TabList px={6} borderBottomWidth="1px" borderColor="border.default" bg="white">
                        <Tab
                            flex={1}
                            fontSize="sm"
                            fontWeight="semibold"
                            color="gray.600"
                            _selected={{ color: 'orange.500', borderBottomColor: 'orange.500' }}
                        >
                            <HStack spacing={1.5}>
                                <Text as="span">Quoting</Text>
                                <Text as="span" fontSize="xs" color="gray.500">
                                    ({notes.length})
                                </Text>
                            </HStack>
                        </Tab>
                        <Tab
                            flex={1}
                            fontSize="sm"
                            fontWeight="semibold"
                            color="gray.600"
                            isDisabled={!hasSupplierOrders}
                            _selected={{ color: '#0071E3', borderBottomColor: '#0071E3' }}
                        >
                            <HStack spacing={1.5}>
                                <Text as="span">Messages</Text>
                                <Text as="span" fontSize="xs" color="gray.500">
                                    ({totalSupplierMessageCount})
                                </Text>
                            </HStack>
                        </Tab>
                    </TabList>
                    <TabPanels flex={1} minH={0} bg="white">
                        <TabPanel px={5} py={5} h="100%" overflowY="auto" bg="white">
                            <VStack align="stretch" spacing={4}>
                                {notes.length === 0 ? (
                                    <Text fontSize="sm" color="gray.500">
                                        No supplier notes found for this order.
                                    </Text>
                                ) : (
                                    <VStack align="stretch" spacing={4}>
                                        {notes.map((note: SupplierNote, index: number) => {
                                            const showDayDivider = index === 0 ||
                                                !isSameDay(notes[index - 1].timeReceived, note.timeReceived);
                                            return (
                                                <Fragment key={note._id}>
                                                    {showDayDivider && (
                                                        <Flex w="100%" justify="center" py={2}>
                                                            <Text
                                                                fontSize="11px"
                                                                color="gray.500"
                                                                fontWeight="medium"
                                                                bg="white"
                                                                borderWidth="1px"
                                                                borderColor="gray.200"
                                                                borderRadius="full"
                                                                px={3}
                                                                py={0.5}
                                                            >
                                                                {formatDayDivider(note.timeReceived)}
                                                            </Text>
                                                        </Flex>
                                                    )}
                                                    <HStack alignSelf="flex-start" align="flex-end" spacing={2} maxW="80%">
                                                        <Avatar
                                                            size="xs"
                                                            name={note.supplierName}
                                                            bg="orange.400"
                                                            color="white"
                                                            flexShrink={0}
                                                        />
                                                        <VStack align="flex-start" spacing={1} minW={0}>
                                                            <Box
                                                                bg="white"
                                                                color="gray.700"
                                                                borderWidth="1px"
                                                                borderColor="gray.200"
                                                                borderRadius="2xl"
                                                                borderBottomLeftRadius="md"
                                                                px={4}
                                                                py={2.5}
                                                                boxShadow="none"
                                                            >
                                                                <Text fontSize="11px" fontWeight="semibold" color="orange.500" mb={0.5}>
                                                                    {note.supplierName}
                                                                </Text>
                                                                <Text fontSize="sm" whiteSpace="pre-wrap" lineHeight="1.5">
                                                                    {note.question}
                                                                </Text>
                                                            </Box>
                                                            <Text fontSize="10px" color="gray.400" px={1}>
                                                                {formatMessageTime(note.timeReceived)}
                                                            </Text>
                                                        </VStack>
                                                    </HStack>
                                                </Fragment>
                                            );
                                        })}
                                    </VStack>
                                )}
                            </VStack>
                        </TabPanel>
                        <TabPanel p={0} h="100%" display="flex" flexDirection="column" minH={0} bg="white">
                            {!hasSupplierOrders ? (
                                <Flex flex={1} align="center" justify="center" px={6}>
                                    <Text fontSize="sm" color="gray.500" textAlign="center">
                                        No supplier orders found for this order.
                                    </Text>
                                </Flex>
                            ) : selectedSupplierOrder ? (
                                <>
                                    <Flex
                                        px={4}
                                        py={3}
                                        borderBottomWidth="1px"
                                        borderColor="gray.200"
                                        justify={embedded ? 'flex-start' : 'space-between'}
                                        align="center"
                                    >
                                        {embedded ? (
                                            <HStack spacing={2.5} minW={0} flex={1}>
                                                <IconButton
                                                    aria-label="Back to supplier list"
                                                    icon={<Icon as={TbArrowLeft as React.ElementType} w={4} h={4} />}
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => setSelectedSupplierOrderId(null)}
                                                    flexShrink={0}
                                                />
                                                <Tooltip label={selectedSupplierOrder.supplierName} placement="top" openDelay={300}>
                                                    <Box flexShrink={0}>
                                                        <SourceBadge source={selectedSupplierOrder.source} />
                                                    </Box>
                                                </Tooltip>
                                                {selectedSupplierOrder.displayId && (
                                                    <Text
                                                        fontSize="10px"
                                                        color="gray.500"
                                                        fontWeight="medium"
                                                        bg="gray.50"
                                                        border="1px solid"
                                                        borderColor="gray.200"
                                                        borderRadius="md"
                                                        px={1}
                                                        py={0.5}
                                                        lineHeight="1.2"
                                                        flexShrink={0}
                                                        noOfLines={1}
                                                    >
                                                        {selectedSupplierOrder.displayId}
                                                    </Text>
                                                )}
                                                <SupplierOrderInfoPopover
                                                    label="Contact Info"
                                                    phone={selectedSupplierOrder.phoneNumber}
                                                    email={selectedSupplierOrder.email}
                                                    compact
                                                />
                                                <Tag
                                                    size="sm"
                                                    colorScheme={getStatusColor(selectedSupplierOrder.status)}
                                                    variant="subtle"
                                                    px={1.5}
                                                    fontSize="11px"
                                                    flexShrink={0}
                                                >
                                                    {getSupplierStatusLabel(selectedSupplierOrder.status)}
                                                </Tag>
                                            </HStack>
                                        ) : (
                                            <>
                                        <HStack spacing={2} minW={0}>
                                            <IconButton
                                                aria-label="Back to supplier list"
                                                icon={<Icon as={TbArrowLeft as React.ElementType} w={4} h={4} />}
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setSelectedSupplierOrderId(null)}
                                                flexShrink={0}
                                            />
                                            <SourceBadge source={selectedSupplierOrder.source} />
                                            <Tooltip label={selectedSupplierOrder.supplierName} placement="top" openDelay={300}>
                                                <Text
                                                    fontSize="sm"
                                                    fontWeight="semibold"
                                                    color="gray.800"
                                                    noOfLines={1}
                                                    minW={0}
                                                >
                                                    {selectedSupplierOrder.supplierName}
                                                </Text>
                                            </Tooltip>
                                            {selectedSupplierOrder.displayId && (
                                                <Text
                                                    fontSize="xs"
                                                    color="gray.500"
                                                    fontWeight="medium"
                                                    bg="gray.50"
                                                    border="1px solid"
                                                    borderColor="gray.200"
                                                    borderRadius="md"
                                                    px={1.5}
                                                    py={0.5}
                                                    lineHeight="1.2"
                                                    flexShrink={0}
                                                    noOfLines={1}
                                                >
                                                    {selectedSupplierOrder.displayId}
                                                </Text>
                                            )}
                                        </HStack>
                                        <HStack spacing={2} flexShrink={0}>
                                            <SupplierOrderInfoPopover
                                                label="Contact Info"
                                                phone={selectedSupplierOrder.phoneNumber}
                                                email={selectedSupplierOrder.email}
                                            />
                                            <Tag
                                                size="sm"
                                                colorScheme={getStatusColor(selectedSupplierOrder.status)}
                                                variant="subtle"
                                                px={1.5}
                                                fontSize="11px"
                                            >
                                                {getSupplierStatusLabel(selectedSupplierOrder.status)}
                                            </Tag>
                                        </HStack>
                                            </>
                                        )}
                                    </Flex>
                                    <VStack
                                        flex={1}
                                        align="stretch"
                                        spacing={4}
                                        px={5}
                                        py={5}
                                        overflowY="auto"
                                        bg="white"
                                        minH={0}
                                    >
                                        {loadingBySupplierOrderId[selectedSupplierOrder.supplierOrderId] ? (
                                            <Flex py={10} justify="center">
                                                <Spinner size="sm" color="gray.500" />
                                            </Flex>
                                        ) : failedSupplierOrderIds.has(selectedSupplierOrder.supplierOrderId) ? (
                                            <VStack spacing={3} py={10}>
                                                <Text fontSize="sm" color="red.500">
                                                    Failed to load supplier messages.
                                                </Text>
                                                <Button
                                                    size="sm"
                                                    onClick={() => void fetchMessagesForSupplierOrder(selectedSupplierOrder, { forceRefresh: true })}
                                                >
                                                    Retry
                                                </Button>
                                            </VStack>
                                        ) : selectedSupplierOrderMessages.length === 0 ? (
                                            <Flex py={10} justify="center">
                                                <Text fontSize="sm" color="gray.500">
                                                    No messages yet.
                                                </Text>
                                            </Flex>
                                        ) : (
                                            selectedSupplierOrderMessages.map((message, index) => {
                                                const isOurMessage = !message.fromSupplier;
                                                const showDayDivider = index === 0 ||
                                                    !isSameDay(selectedSupplierOrderMessages[index - 1].timestamp, message.timestamp);
                                                const sentByCurrentUser =
                                                    isOurMessage &&
                                                    !!sessionContext?.isAuthenticated &&
                                                    !!message.sentByUserId &&
                                                    message.sentByUserId === sessionContext.userId;
                                                let displayName: string;
                                                if (isOurMessage) {
                                                    if (message.sentByUserName) {
                                                        displayName = sentByCurrentUser
                                                            ? `${message.sentByUserName} (you)`
                                                            : message.sentByUserName;
                                                    } else if (message.senderName) {
                                                        // Sent directly in the supplier platform (not via Partbay) —
                                                        // show the real sender name from the source thread.
                                                        displayName = message.senderName;
                                                    } else {
                                                        displayName = sentByCurrentUser ? 'You' : 'Partbay user';
                                                    }
                                                } else {
                                                    displayName = message.senderName || selectedSupplierOrder.supplierName;
                                                }
                                                const avatarName = message.sentByUserName
                                                    ?? message.senderName
                                                    ?? displayName.replace(/\s+\(you\)$/, '');
                                                return (
                                                    <Fragment key={`${selectedSupplierOrder.supplierOrderId}-${message._id}`}>
                                                        {showDayDivider && (
                                                            <Flex w="100%" justify="center" py={2}>
                                                                <Text
                                                                    fontSize="11px"
                                                                    color="gray.500"
                                                                    fontWeight="medium"
                                                                    bg="white"
                                                                    borderWidth="1px"
                                                                    borderColor="gray.200"
                                                                    borderRadius="full"
                                                                    px={3}
                                                                    py={0.5}
                                                                >
                                                                    {formatDayDivider(message.timestamp)}
                                                                </Text>
                                                            </Flex>
                                                        )}
                                                        <HStack
                                                            alignSelf={isOurMessage ? 'flex-end' : 'flex-start'}
                                                            align="flex-end"
                                                            spacing={2}
                                                            maxW="80%"
                                                            flexDirection={isOurMessage ? 'row-reverse' : 'row'}
                                                        >
                                                            <Avatar
                                                                size="xs"
                                                                name={avatarName}
                                                                bg={isOurMessage ? '#0071E3' : 'orange.400'}
                                                                color="white"
                                                                flexShrink={0}
                                                            />
                                                            <VStack align={isOurMessage ? 'flex-end' : 'flex-start'} spacing={1} minW={0}>
                                                                <Box
                                                                    bg="white"
                                                                    color="gray.700"
                                                                    borderWidth="1px"
                                                                    borderColor="gray.200"
                                                                    borderRadius="2xl"
                                                                    borderBottomRightRadius={isOurMessage ? 'md' : '2xl'}
                                                                    borderBottomLeftRadius={isOurMessage ? '2xl' : 'md'}
                                                                    px={4}
                                                                    py={2.5}
                                                                    boxShadow="none"
                                                                >
                                                                    <Text fontSize="11px" fontWeight="semibold" color={isOurMessage ? '#0071E3' : 'orange.500'} mb={0.5}>
                                                                        {displayName}
                                                                    </Text>
                                                                    <Text fontSize="sm" whiteSpace="pre-wrap" lineHeight="1.5">
                                                                        {message.comment}
                                                                    </Text>
                                                                </Box>
                                                                <Text fontSize="10px" color="gray.400" px={1}>
                                                                    {formatMessageTime(message.timestamp)}
                                                                </Text>
                                                            </VStack>
                                                        </HStack>
                                                    </Fragment>
                                                );
                                            })
                                        )}
                                    </VStack>
                                    <Box px={4} pt={4} pb={4} borderTopWidth="1px" borderColor="gray.200" bg="white" flexShrink={0}>
                                        <Box position="relative">
                                            <Textarea
                                                value={newMessage}
                                                onChange={(event) => setNewMessage(event.target.value)}
                                                onKeyDown={handleMessageKeyDown}
                                                placeholder="Send a message..."
                                                resize="none"
                                                minH="80px"
                                                pr="44px"
                                                pb="34px"
                                                fontSize="sm"
                                                borderRadius="xl"
                                                borderColor="gray.300"
                                                _focus={{ borderColor: '#0071E3', boxShadow: '0 0 0 1px #0071E3' }}
                                                isDisabled={isSendingMessage}
                                            />
                                            <IconButton
                                                aria-label="Send message"
                                                icon={<Icon as={TbArrowUp as React.ElementType} w={4} h={4} />}
                                                type="button"
                                                position="absolute"
                                                bottom="8px"
                                                right="8px"
                                                zIndex={2}
                                                size="sm"
                                                borderRadius="full"
                                                bg="#0071E3"
                                                color="white"
                                                _hover={{ bg: '#3544a0' }}
                                                _disabled={{
                                                    bg: '#0071E3',
                                                    opacity: 0.6,
                                                    cursor: 'not-allowed',
                                                }}
                                                minW="28px"
                                                h="28px"
                                                onMouseDown={(event) => event.preventDefault()}
                                                onClick={() => void handleSendMessage()}
                                                isLoading={isSendingMessage}
                                                isDisabled={!newMessage.trim()}
                                            />
                                        </Box>
                                        <Text fontSize="xs" color="gray.400" mt={1.5} ml={1}>
                                            Press Ctrl+Enter to send
                                        </Text>
                                    </Box>
                                </>
                            ) : (
                                <VStack align="stretch" spacing={3} p={4} overflowY="auto" bg="white">
                                    {isLoadingAllMessages && (
                                        <HStack spacing={2} color="gray.500" fontSize="sm" py={1}>
                                            <Spinner size="xs" />
                                            <Text>Syncing supplier messages...</Text>
                                        </HStack>
                                    )}
                                    {supplierOrders.map((supplierOrder) => {
                                        const messages = messagesBySupplierOrderId[supplierOrder.supplierOrderId] ?? [];
                                        const supplierMessageCount = messages.length;
                                        const lastMessage = messages[messages.length - 1];
                                        const hasFailed = failedSupplierOrderIds.has(supplierOrder.supplierOrderId);
                                        const isLoading = loadingBySupplierOrderId[supplierOrder.supplierOrderId];
                                        return (
                                            <Box
                                                key={supplierOrder.supplierOrderId}
                                                p={4}
                                                position="relative"
                                                borderRadius="md"
                                                border="1px solid"
                                                borderColor="gray.200"
                                                bg="white"
                                                cursor="pointer"
                                                _hover={{ borderColor: '#0071E3', boxShadow: 'none' }}
                                                onClick={() => setSelectedSupplierOrderId(supplierOrder.supplierOrderId)}
                                            >
                                                <HStack justify="space-between" align="flex-start" spacing={2}>
                                                    <VStack align="flex-start" spacing={1} minW={0} flex={1}>
                                                        <HStack spacing={2} minW={0} w="100%">
                                                            <SourceBadge source={supplierOrder.source} />
                                                            <Tooltip label={supplierOrder.supplierName} placement="top" openDelay={300}>
                                                                <Text fontSize="md" fontWeight="semibold" color="gray.800" noOfLines={1} minW={0}>
                                                                    {supplierOrder.supplierName}
                                                                </Text>
                                                            </Tooltip>
                                                            {supplierOrder.displayId && (
                                                                <Text
                                                                    fontSize="xs"
                                                                    color="gray.500"
                                                                    fontWeight="medium"
                                                                    bg="gray.50"
                                                                    border="1px solid"
                                                                    borderColor="gray.200"
                                                                    borderRadius="md"
                                                                    px={1.5}
                                                                    py={0.5}
                                                                    lineHeight="1.2"
                                                                    flexShrink={0}
                                                                    noOfLines={1}
                                                                >
                                                                    {supplierOrder.displayId}
                                                                </Text>
                                                            )}
                                                        </HStack>
                                                        {(supplierOrder.phoneNumber || supplierOrder.email) && (
                                                            <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                                                {supplierOrder.phoneNumber ? formatPhoneNumber(supplierOrder.phoneNumber) : ''}
                                                                {supplierOrder.phoneNumber && supplierOrder.email ? ' • ' : ''}
                                                                {supplierOrder.email ?? ''}
                                                            </Text>
                                                        )}
                                                        <Text fontSize="xs" color={hasFailed ? 'red.500' : 'gray.500'} noOfLines={1}>
                                                            {hasFailed
                                                                ? 'Unable to load messages'
                                                                : isLoading
                                                                    ? 'Loading messages...'
                                                                    : lastMessage?.comment || 'No messages yet'}
                                                        </Text>
                                                    </VStack>
                                                    <VStack spacing={1} align="flex-end" flexShrink={0}>
                                                        <Tag
                                                            size="sm"
                                                            colorScheme={getStatusColor(supplierOrder.status)}
                                                            variant="subtle"
                                                            px={1.5}
                                                            fontSize="11px"
                                                        >
                                                            {getSupplierStatusLabel(supplierOrder.status)}
                                                        </Tag>
                                                    </VStack>
                                                </HStack>
                                                {supplierMessageCount > 0 && (
                                                    <Flex
                                                        position="absolute"
                                                        bottom="10px"
                                                        right="10px"
                                                        minW="18px"
                                                        h="18px"
                                                        px="5px"
                                                        borderRadius="full"
                                                        bg="#0071E3"
                                                        color="white"
                                                        align="center"
                                                        justify="center"
                                                        fontSize="10px"
                                                        fontWeight="bold"
                                                        lineHeight="1"
                                                    >
                                                        {supplierMessageCount}
                                                    </Flex>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </VStack>
                            )}
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            </Box>
    );

    const embeddedPanel = (
        <Box h="100%" display="flex" flexDirection="column" bg="white">
            <Flex
                px={6}
                h="topbar"
                borderBottomWidth="1px"
                borderColor="border.subtle"
                bg="white"
                align="center"
                justify="space-between"
                flexShrink={0}
            >
                <HStack spacing={2}>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700">Supplier Communications</Text>
                    <IconButton
                        aria-label="Refresh supplier messages"
                        icon={<Icon as={TbRefresh as React.ElementType} w={4} h={4} />}
                        size="sm"
                        variant="ghost"
                        onClick={() => void handleRefreshMessages()}
                        isLoading={
                            selectedSupplierOrder
                                ? !!loadingBySupplierOrderId[selectedSupplierOrder.supplierOrderId]
                                : isLoadingAllMessages
                        }
                        isDisabled={activeTab !== 'order-messages' || !hasSupplierOrders}
                    />
                </HStack>
                {showOpenInChats && (
                    <Tooltip label="Open in Chats" placement="bottom">
                        <IconButton
                            aria-label="Open in Chats"
                            icon={<YaxleIcon name="expand" size="sm" />}
                            size="sm"
                            variant="ghost"
                            color="fg.muted"
                            onClick={openInChats}
                        />
                    </Tooltip>
                )}
            </Flex>
            {tabsContent}
        </Box>
    );

    if (embedded) {
        return embeddedPanel;
    }

    return (
        <AppDrawer
            isOpen={isOpen}
            onClose={onClose}
            size="md"
            title="Supplier Communications"
            hideOverlay={hideOverlay}
            headerRight={
                <HStack spacing={1}>
                    <IconButton
                        aria-label="Refresh supplier messages"
                        icon={<Icon as={TbRefresh as React.ElementType} w={4} h={4} />}
                        size="sm"
                        variant="ghost"
                        onClick={() => void handleRefreshMessages()}
                        isLoading={
                            selectedSupplierOrder
                                ? !!loadingBySupplierOrderId[selectedSupplierOrder.supplierOrderId]
                                : isLoadingAllMessages
                        }
                        isDisabled={activeTab !== 'order-messages' || !hasSupplierOrders}
                    />
                    {showOpenInChats && (
                        <Tooltip label="Open in Chats" placement="bottom">
                            <IconButton
                                aria-label="Open in Chats"
                                icon={<YaxleIcon name="expand" size="sm" />}
                                size="sm"
                                variant="ghost"
                                color="fg.muted"
                                onClick={openInChats}
                            />
                        </Tooltip>
                    )}
                </HStack>
            }
            bodyProps={{ p: 0, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', bg: 'white' }}
            contentProps={{
                overflow: 'hidden',
                ...(hideOverlay ? { containerProps: SIDE_DRAWER_CONTAINER_PROPS } : {}),
                motionProps: INSTANT_SIDE_DRAWER_MOTION_PROPS,
            }}
        >
            {tabsContent}
        </AppDrawer>
    );
};

export default SupplierNotesModal;
